import {pipelineHasStep} from 'analysis-tool/data/getStepFromAnalysis';
import {getMainViewItemForView, getViewForViewReference} from 'analysis-tool/data-view/AnalysisViewUtils';
import {GetAnalysis} from 'analysis-tool/graphql/__generated/GetAnalysis';
import {
  AnalysisMainViewItem,
  AnalysisMainViewItemType,
  isStepAwareAnalysisItem,
  ViewReference,
} from 'analysis-tool/ui/main-view/MainViewTypes';
import {decodeJQueryParam, parseUri, setURLParams} from 'BenchlingUtil';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {getViewForOutputDataId} from 'pipelines/PipelineViewUtils';

// Keep in sync with AnalysisUrlScreen in benchling/analysis_tool/analysis_urls.py
export type AnalysisScreenParam = 'flowchart' | 'view' | 'saveToResults' | 'sendToDocument' | 'regression';

/**
 * Central mapping from linkable AnalysisMainViewItemType to URL screen parameter value.
 * This is the source of truth for which item types support deep linking.
 * Adding a new entry here automatically makes it a valid screen param for parsing.
 */
const ITEM_TYPE_TO_SCREEN: Partial<Record<AnalysisMainViewItemType, AnalysisScreenParam>> = {
  [AnalysisMainViewItemType.FLOWCHART]: 'flowchart',
  [AnalysisMainViewItemType.DATA_VIEW]: 'view',
  [AnalysisMainViewItemType.SAVE_TO_RESULTS_OUTPUT]: 'saveToResults',
  [AnalysisMainViewItemType.SEND_TO_DOCUMENT]: 'sendToDocument',
  [AnalysisMainViewItemType.REGRESSION]: 'regression',
};

const VALID_SCREEN_VALUES: ReadonlySet<string> = new Set(Object.values(ITEM_TYPE_TO_SCREEN));

export interface AnalysisQueryParameters {
  screen: AnalysisScreenParam | null;
  stepId: string | null;
  stepDataName: string | null;
  rawDataIndex: number | null;
  highlightedStepIds: Array<string> | null;
  chartId: string | null;
  dataFrameId: string | null;
  fileId: string | null;
  showChips: boolean | null;
}

export function parseAnalyticsUrlQueryParameters(): AnalysisQueryParameters {
  const {queryKey} = parseUri(window.location.href);
  const rawScreen = queryKey.screen ?? null;
  const rawRawDataIndex = queryKey.rawDataIndex ?? null;
  const rawStepDataName = queryKey.stepDataName ?? null;
  const rawHighlightedStepIds = queryKey.highlightedStepIds ?? null;
  return {
    screen:
      rawScreen != null && VALID_SCREEN_VALUES.has(rawScreen) ? (rawScreen as AnalysisScreenParam) : null,
    stepId: queryKey.stepId ?? null,
    // parseUri returns raw (URL-encoded) query values. stepDataName may contain '+' for spaces
    // (e.g., "Output+files"), so decode it properly using decodeJQueryParam which handles + → space.
    stepDataName: rawStepDataName != null ? decodeJQueryParam(rawStepDataName) : null,
    rawDataIndex:
      rawRawDataIndex != null
        ? Number.isFinite(Number(rawRawDataIndex))
          ? parseInt(rawRawDataIndex, 10)
          : null
        : null,
    // parseUri returns raw (URL-encoded) values, so commas may be encoded as %2C.
    // Decode before splitting to correctly parse "anastep_1%2Canastep_2" → ["anastep_1", "anastep_2"].
    // Normalize empty arrays to null so callers don't accidentally serialize "highlightedStepIds=".
    highlightedStepIds: (() => {
      if (rawHighlightedStepIds == null) {
        return null;
      }
      const ids = decodeJQueryParam(rawHighlightedStepIds)
        .split(',')
        .filter((s) => Boolean(s));
      return ids.length > 0 ? ids : null;
    })(),
    chartId: queryKey.chartId ?? null,
    dataFrameId: queryKey.dataFrameId ?? null,
    fileId: queryKey.fileId ?? null,
    showChips: queryKey.showChips === 'true' ? true : null,
  };
}

/**
 * Inverse of ITEM_TYPE_TO_SCREEN for step-aware types (screen → item type).
 * Excludes FLOWCHART and DATA_VIEW which have custom parsing logic.
 */
const SCREEN_TO_STEP_AWARE_TYPE = new Map<string, AnalysisMainViewItemType>(
  Object.entries(ITEM_TYPE_TO_SCREEN)
    .filter(
      ([type]) => type !== AnalysisMainViewItemType.FLOWCHART && type !== AnalysisMainViewItemType.DATA_VIEW
    )
    .map(([type, screen]) => [screen, type as AnalysisMainViewItemType])
);

/**
 * Derive the initial AnalysisMainViewItem from URL query parameters.
 * Returns null if no valid deep link is specified or the params are invalid for the given analysis.
 */
export function getMainViewItemFromUrlParams(
  params: AnalysisQueryParameters,
  analysis: PipelineGraphQL
): AnalysisMainViewItem | null {
  const {screen, stepId, stepDataName, rawDataIndex, highlightedStepIds, chartId, dataFrameId, fileId} =
    params;

  if (screen === 'flowchart') {
    return {
      type: AnalysisMainViewItemType.FLOWCHART,
      ...(highlightedStepIds != null && highlightedStepIds.length > 0 ? {highlightedStepIds} : {}),
    };
  }

  if (screen === 'view') {
    const {showChips} = params;
    // Full or partial step reference: stepId with optional outputReference
    if (stepId != null && pipelineHasStep(analysis, stepId)) {
      const viewReference: ViewReference = {
        stepId,
        outputReference: stepDataName != null ? {stepDataName, rawDataIndex} : null,
      };
      const view = getViewForViewReference(analysis, viewReference);
      if (view != null) {
        return {type: AnalysisMainViewItemType.DATA_VIEW, viewReference, showChips};
      }
    }

    // Subset linking: find view by output data ID (chartId, dataFrameId, or fileId)
    const outputDataId = chartId ?? dataFrameId ?? fileId;
    if (outputDataId != null) {
      const view = getViewForOutputDataId(analysis, outputDataId);
      if (view != null) {
        const item = getMainViewItemForView(view, analysis.steps);
        if (item.type === AnalysisMainViewItemType.DATA_VIEW) {
          return {...item, showChips};
        }
      }
    }
  }

  // Step-aware output screens (saveToResults, sendToDocument, regression)
  if (screen != null && stepId != null && pipelineHasStep(analysis, stepId)) {
    const itemType = SCREEN_TO_STEP_AWARE_TYPE.get(screen);
    if (itemType != null) {
      return {type: itemType, stepId} as AnalysisMainViewItem;
    }
  }

  return null;
}

/**
 * URL query parameter values representing a deep link to an analysis screen.
 * Null values indicate the parameter should be removed from the URL.
 */
export interface AnalysisDeepLinkParams {
  screen: string | null;
  stepId: string | null;
  stepDataName: string | null;
  rawDataIndex: number | null;
  highlightedStepIds: string | null;
  chartId: string | null;
  dataFrameId: string | null;
  fileId: string | null;
  showChips: boolean | null;
}

const NULL_DEEP_LINK_PARAMS: AnalysisDeepLinkParams = {
  screen: null,
  stepId: null,
  stepDataName: null,
  rawDataIndex: null,
  highlightedStepIds: null,
  chartId: null,
  dataFrameId: null,
  fileId: null,
  showChips: null,
};

/**
 * Derive URL query parameter values from the current selected item.
 * Linkable types: FLOWCHART, DATA_VIEW, and output screens (SAVE_TO_RESULTS_OUTPUT, SEND_TO_DOCUMENT,
 * REGRESSION). All other types return null params (removing them from the URL).
 */
export function getUrlParamsFromSelectedItem(selectedItem: AnalysisMainViewItem): AnalysisDeepLinkParams {
  switch (selectedItem.type) {
    case AnalysisMainViewItemType.FLOWCHART:
      return {
        ...NULL_DEEP_LINK_PARAMS,
        screen: 'flowchart',
        highlightedStepIds:
          selectedItem.highlightedStepIds != null && selectedItem.highlightedStepIds.length > 0
            ? selectedItem.highlightedStepIds.join(',')
            : null,
      };
    case AnalysisMainViewItemType.DATA_VIEW:
      return {
        ...NULL_DEEP_LINK_PARAMS,
        screen: 'view',
        stepId: selectedItem.viewReference.stepId,
        stepDataName: selectedItem.viewReference.outputReference?.stepDataName ?? null,
        rawDataIndex: selectedItem.viewReference.outputReference?.rawDataIndex ?? null,
      };
    default: {
      if (isStepAwareAnalysisItem(selectedItem)) {
        const screenParam = ITEM_TYPE_TO_SCREEN[selectedItem.type];
        if (screenParam != null) {
          return {...NULL_DEEP_LINK_PARAMS, screen: screenParam, stepId: selectedItem.stepId};
        }
      }
      return NULL_DEEP_LINK_PARAMS;
    }
  }
}

/**
 * Returns the analysis url with any applicable query parameters. If the analysis is inaccessible, then we
 * return undefined, which matches the expected behavior in the Analysis route
 */
export function getFullAnalysisUrl(analysis: GetAnalysis['analysis']): string | undefined {
  if (isInaccessible(analysis)) {
    return undefined;
  }

  const {
    screen,
    stepId,
    stepDataName,
    rawDataIndex,
    highlightedStepIds,
    chartId,
    dataFrameId,
    fileId,
    showChips,
  } = parseAnalyticsUrlQueryParameters();
  const validStepId = stepId && pipelineHasStep(analysis, stepId) ? stepId : null;

  // Screens that require a valid step (everything except flowchart) should be nulled
  // when the step doesn't exist in the analysis, to avoid generating orphaned URLs like "?screen=view"
  // that won't round-trip through getMainViewItemFromUrlParams.
  // Exception: screen=view with an output data ID (chartId/dataFrameId/fileId) doesn't need a stepId.
  const screenRequiresStep = screen != null && screen !== 'flowchart';
  const hasOutputDataId = screen === 'view' && (chartId != null || dataFrameId != null || fileId != null);
  const validScreen = screenRequiresStep && validStepId == null && !hasOutputDataId ? null : screen;

  const url = setURLParams(analysis.url, {
    screen: validScreen,
    stepId: validStepId,
    // Only include view-specific params when screen=view and the step is valid
    stepDataName: validScreen === 'view' && validStepId != null ? stepDataName : null,
    rawDataIndex: validScreen === 'view' && validStepId != null ? rawDataIndex : null,
    // Only include highlightedStepIds when screen=flowchart
    highlightedStepIds:
      validScreen === 'flowchart' && highlightedStepIds != null && highlightedStepIds.length > 0
        ? highlightedStepIds.join(',')
        : null,
    // Only include output data ID params when screen=view and no stepId is present (subset linking)
    chartId: validScreen === 'view' && validStepId == null ? chartId : null,
    dataFrameId: validScreen === 'view' && validStepId == null ? dataFrameId : null,
    fileId: validScreen === 'view' && validStepId == null ? fileId : null,
    showChips: validScreen === 'view' ? showChips : null,
  });

  return url;
}
